import React, { useEffect, createElement } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { CheckCircle2, Shield, Clock, TrendingUp } from 'lucide-react';
export const ConsultationPage = () => {
  // Load Calendly script on mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // Cleanup script if component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  return <div className="font-sans text-black min-h-screen flex flex-col bg-gray-50" data-id="element-182">
      <Header data-id="element-183" />

      <main className="flex-grow" data-id="element-184">
        {/* Hero Section */}
        <section className="relative bg-[#1a2e1c] text-white pt-32 pb-20 md:pt-40 md:pb-24 overflow-hidden" data-id="element-185">
          {/* Background texture */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(216,163,77,0.06)_0%,transparent_50%)] pointer-events-none" data-id="element-186"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(216,163,77,0.04)_0%,transparent_50%)] pointer-events-none" data-id="element-187"></div>

          <div className="container mx-auto px-4 md:px-8 relative z-10" data-id="element-188">
            <div className="max-w-4xl mx-auto text-center" data-id="element-189">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-[1.1] mb-6 tracking-tight" data-id="element-190">
                Book Your Private{' '}
                <span className="text-[#d8a34d]" data-id="element-191">Strategy Session</span>
              </h1>
              <p className="text-white/70 text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto" data-id="element-192">
                Speak directly with our Dubai real estate experts to build a
                tailored investment portfolio that matches your goals.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content Area */}
        <section className="py-12 md:py-20" data-id="element-193">
          <div className="container mx-auto px-4 md:px-8" data-id="element-194">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-195">
              {/* Left Column - Value Prop & Trust */}
              <div className="lg:col-span-5 space-y-10" data-id="element-196">
                <div data-id="element-197">
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-[#1a2e1c] mb-6" data-id="element-198">
                    What to expect on our call
                  </h2>

                  <ul className="space-y-6" data-id="element-199">
                    <li className="flex gap-4" data-id="element-200">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d8a34d]/10 flex items-center justify-center" data-id="element-201">
                        <TrendingUp className="w-5 h-5 text-[#d8a34d]" data-id="element-202" />
                      </div>
                      <div data-id="element-203">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-204">
                          Market Analysis
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-205">
                          Current trends, high-yield areas, and upcoming
                          developments in Dubai.
                        </p>
                      </div>
                    </li>

                    <li className="flex gap-4" data-id="element-206">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d8a34d]/10 flex items-center justify-center" data-id="element-207">
                        <CheckCircle2 className="w-5 h-5 text-[#d8a34d]" data-id="element-208" />
                      </div>
                      <div data-id="element-209">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-210">
                          Portfolio Strategy
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-211">
                          Tailored recommendations based on your budget,
                          timeline, and ROI goals.
                        </p>
                      </div>
                    </li>

                    <li className="flex gap-4" data-id="element-212">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d8a34d]/10 flex items-center justify-center" data-id="element-213">
                        <Shield className="w-5 h-5 text-[#d8a34d]" data-id="element-214" />
                      </div>
                      <div data-id="element-215">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-216">
                          Legal & Tax Guidance
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-217">
                          Clear explanation of the buying process, Golden Visa
                          requirements, and tax benefits.
                        </p>
                      </div>
                    </li>

                    <li className="flex gap-4" data-id="element-218">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d8a34d]/10 flex items-center justify-center" data-id="element-219">
                        <Clock className="w-5 h-5 text-[#d8a34d]" data-id="element-220" />
                      </div>
                      <div data-id="element-221">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-222">
                          Payment Plans
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-223">
                          Breakdown of flexible developer payment structures and
                          financing options.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Trust Badges */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-224">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-6 text-center" data-id="element-225">
                    Regulated & Compliant
                  </h3>
                  <div className="flex items-center justify-center gap-8" data-id="element-226">
                    <img src="https://cdn.magicpatterns.com/uploads/cu3MDDjCGv6evPcHWpiV4U/Dubai_Land_Department_compliant.png" alt="Dubai Land Department" className="h-12 object-contain grayscale hover:grayscale-0 transition-all" data-id="element-227" />
                    <div className="w-px h-10 bg-gray-200" data-id="element-228"></div>
                    <img src="https://cdn.magicpatterns.com/uploads/1xJZhj4hfNVptFz8oMkj5q/rera_1-removebg-preview.png" alt="RERA" className="h-12 object-contain grayscale hover:grayscale-0 transition-all" data-id="element-229" />
                  </div>
                </div>
              </div>

              {/* Right Column - Calendly Embed */}
              <div className="lg:col-span-7" data-id="element-230">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 min-h-[700px]" data-id="element-231">
                  {/* Calendly inline widget begin */}
                  <div className="calendly-inline-widget w-full h-[700px]" data-url="https://calendly.com/ikigairealestate/private-tour-with-ikigai-real-estate?hide_event_type_details=1&hide_gdpr_banner=1" data-id="element-232"></div>
                  {/* Calendly inline widget end */}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-233" />
      <WhatsAppButton data-id="element-234" />
    </div>;
};